<template>

  <MembershipBannerBar v-if="!userStore.isFullMember()">
    Full members can import their data from Letterboxd.
  </MembershipBannerBar>

  <div class="container-fluid" :class="{'showing-message-banner': !userStore.isFullMember()}">
    <div class="mol-center-card-width">
      <div class="title-text">
        <h2>Importing Progress From</h2>
        <ColorThemeImg class="letterboxd-icon" :src="`${$imagePathBase}logos/letterboxd/letterboxd-logo-h-pos-rgb.svg`" :dark_theme_src="`${$imagePathBase}logos/letterboxd/letterboxd-logo-h-neg-rgb.svg`"/>
      </div>
    </div>


    <!--    EXPORT_FROM_LETTERBOXD -->
    <div v-if="state === LBState.EXPORT_FROM_LETTERBOXD" class="mol-center-card-width export-from-letterboxd">
      <div>
        <p>If you have an account with the popular movie social media site <i>Letterboxd</i>, we have good news!</p>
        <p><i>Letterboxd</i> offers a convenient way for you to export the films you've marked as viewed, as well as films on your watchlist.</p>
        <p><i>myOscarList</i> offers a convenient way to import them, saving you a lot of time when you're initially checking off films you've already seen.</p>
      </div>

      <div v-if="!userStore.isFullMember()" class="membership-message">
        <h4>
          If you'd like to import your data,<br>
          explore <router-link to="/membership">membership</router-link> today.
        </h4>
      </div>

      <div v-else class="import-steps">
        <div class="import-step">
          <h4>First, export your data from <i>Letterboxd</i></h4>
          <p>
            In <i>Letterboxd</i>, navigate to the <b>data</b> section of your
            <a class="account-settings-link" href="https://letterboxd.com/settings/data/" title='letterboxd-data' target='blank'>
              account settings<sup><i style="" class='fa fa-external-link'></i></sup>
            </a>
            page.
          </p>
          <p>Find the button that says <span class="export-your-data">export your data</span> and click it.</p>
          <p>This will take you through a few steps resulting in a file with a <b>.zip</b> extension being saved locally.</p>
        </div>

        <div class="import-step">
          <h4>Then, import that data into <i>myOscarList</i></h4>
          <p></p>

          <p>Choose the downloaded <b>.zip</b> file here.</p>
          <div class="import-button">
            <button class="btn btn-sm btn-primary" type="button" @click="$refs['letterboxd-file-input'].click()">
              find <b>.zip</b> file to import
            </button>
            <input ref="letterboxd-file-input" class="form-control" type="file" id="formFile" accept=".zip">
          </div>
          <p>Don't worry, you'll have an opportunity to review the data before it's imported.</p>
        </div>

      </div>
    </div>


    <!--    IMPORT_TO_MYOSCARLIST -->
    <div v-if="state === LBState.IMPORT_TO_MYOSCARLIST" class="mol-center-card-width import-to-myoscarlist">

      <!-- films watched -->
      <div class="import-watched-films">
        <ExpandableCard class="expandable-card mol-center-card-width films-viewed-card">

          <template #header>
            <section class="expandable-card-header">
              <h5>Films marked <span class="letterboxd-watched"><img :src="`${$imagePathBase}logos/letterboxd/watched.svg`"> Watched</span> on Letterboxd<br></h5>
            </section>
          </template>

          <template #body>
            <LoadingSpinner v-if="!filmsToMarkViewed" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>
            <div v-else-if="filmsToMarkViewed.length === 0">
              No Oscar Winners or Nominees Found
            </div>
            <div v-else class="film-list">
              <div v-for="film in filmsToMarkViewed" :key="film._id" >
                <FilmLink :film_name="film.name" :film_id="film._id" single_line="true" :link_location="`Letterboxd Import: ${user_id}`" no_strikethrough="true"></FilmLink>
              </div>
            </div>
          </template>

        </ExpandableCard>

        <div v-if="filmsToMarkViewed?.length > 0" class="import-button-div">
          <p>Importing will mark these films <span class="letterboxd-watched"><i class="fa fa-check-square-o"></i> viewed</span> in myOscarList.</p>
          <button ref="import-viewed-films-button" class="btn btn-sm btn-primary" type="button" @click="onMarkFilmsViewed()">
            Import Viewed Films
          </button>
        </div>
      </div>


      <!-- films on watchlist -->
      <div class="import-bookmarked-films">
        <ExpandableCard class="expandable-card mol-center-card-width films-viewed-card watchlist-card">

          <template #header>
            <section class="expandable-card-header">
              <h5>Films in your <span class="letterboxd-watched"><img :src="`${$imagePathBase}logos/letterboxd/watchlist.svg`">Watchlist</span> on Letterboxd<br></h5>
            </section>
          </template>

          <template #body>
            <LoadingSpinner v-if="!filmsToMarkBookmarked" class="text-secondary" style="margin-top: 10px;"></LoadingSpinner>
            <div v-else-if="filmsToMarkBookmarked.length === 0">
              No Oscar Winners or Nominees Found
            </div>
            <div v-else class="film-list">
              <div v-for="film in filmsToMarkBookmarked" :key="film._id" >
                <FilmLink :film_name="film.name" :film_id="film._id" single_line="true" :link_location="`Letterboxd Import: ${user_id}`" no_strikethrough="true"></FilmLink><!--<i class="fa fa-bookmark"></i>-->
              </div>
            </div>
          </template>

        </ExpandableCard>

        <div v-if="filmsToMarkBookmarked?.length > 0" class="import-button-div">
          <p>Importing will <span class="letterboxd-watched"><i class="fa fa-bookmark"></i> bookmark</span> these films in myOscarList.</p>
          <button ref="import-bookmarked-films-button" class="btn btn-sm btn-primary" type="button" @click="onMarkFilmsBookmarked()">
            Import Bookmarked Films
          </button>
        </div>

      </div>
    </div>





    <!--    ERROR IMPORTING -->
    <div v-if="state === LBState.ERROR" class="mol-center-card-width error-importing">
      <p v-html="statusText"></p>

      <button class="btn btn-sm btn-primary" type="button" @click="$router.go()">
        OK
      </button>

    </div>


  </div>
</template>


<script>

import LoadingSpinner from "@/components/LoadingSpinner.vue";
import JSZip from "jszip";
import Papa from "papaparse";
import axios from "axios";
// eslint-disable-next-line
import {userStore} from "@/userStore";
import ExpandableCard from "@/components/ExpandableCard.vue";
import FilmLink from "@/components/FilmLink.vue";
import analytics from "@/analytics";
import MembershipBannerBar from "@/components/MembershipBannerBar.vue";
import ColorThemeImg from "@/components/ColorThemeImg.vue";

export default {
  name: "LetterboxdPage",
  components: {ColorThemeImg, MembershipBannerBar, FilmLink, ExpandableCard, LoadingSpinner},
  data() {
    return {
      LBState: Object.freeze({
        EXPORT_FROM_LETTERBOXD: Symbol("EXPORT_FROM_LETTERBOXD"),
        IMPORT_TO_MYOSCARLIST: Symbol("IMPORT_TO_MYOSCARLIST"),
        ERROR: Symbol("ERROR"),
      }),
      state: null,
      statusText: '',
      filmsToMarkViewed: null,
      filmsToMarkBookmarked: null
    }
  },
  created() {
    this.state = this.LBState.EXPORT_FROM_LETTERBOXD;
  },
  mounted() {
    this.$refs["letterboxd-file-input"]?.addEventListener('change', this.importLetterboxd);
  },
  beforeUnmount() {
    this.$refs["letterboxd-file-input"]?.removeEventListener('change', this.importLetterboxd);
  },
  computed: {
    userStore() {
      return userStore;
    }
  },
  methods: {
    async importLetterboxd(event) {
      const selectedFile = event?.target?.files[0];
      if ( !selectedFile?.name?.endsWith('.zip') ) {
        return;
      }
      this.state = this.LBState.IMPORT_TO_MYOSCARLIST;

      // read the file data
      return selectedFile.arrayBuffer()
          .then(arrayBuffer => JSZip.loadAsync(arrayBuffer))

          // got the JSZip object
          .then(jsZip => {
            return Promise.all([
              this.getLetterboxdURIsFromJSZip(jsZip, 'watched.csv'),
              this.getLetterboxdURIsFromJSZip(jsZip, 'watchlist.csv')
            ]);
          })

          // got the films in our DB that have been marked 'watched' on Letterboxd
          .then(([viewedURIs, watchlistURIs]) => {
            return Promise.all([
              axios.post('/api/film/names', { letterboxd_uris: viewedURIs }),
              axios.post('/api/film/names', { letterboxd_uris: watchlistURIs })
            ]);
          })
          .then(([viewedResponse, watchlistResponse]) => {
            this.filmsToMarkViewed = viewedResponse.data;
            this.filmsToMarkBookmarked = watchlistResponse.data;
          })

          .catch(err => {
            //console.log(err);
            analytics.notifyBugsnagOfError(err);
            this.statusText = `Error Importing${selectedFile?.name?.length ? ' <b>' + selectedFile.name + '</b>' : ''}`
            this.state = this.LBState.ERROR;
          });
    },

    async getLetterboxdURIsFromJSZip(_jsZip, _documentName) {

      //console.log(`getLetterboxdURIsFromJSZip( '${_documentName}' )`, _jsZip);

      if ( !_jsZip?.files ) {
        throw new Error(`getLetterboxdURIsFromJSZip() was passed an invalid JSZip object.`);
      }
      if ( !_documentName || !_documentName.endsWith('.csv') ) {
        throw new Error(`getLetterboxdURIsFromJSZip() expected to be passed a .csv filename, but was passed '${_documentName}' instead.`)
      }
      if ( !_jsZip.files[_documentName] ) {
        throw new Error(`getLetterboxdURIsFromJSZip() was passed a JSZip object that does not include '${_documentName}'.`)
      }

      return _jsZip.files[_documentName]?.async("text")
          .then(unzippedCsv => {
            //console.log('getLetterboxdURIsFromJSZip()', unzippedCsv);

            if ( !unzippedCsv ) {
              throw new Error(`getLetterboxdURIsFromJSZip() could not read '${_documentName}' from the JSZip object.`);
            }
            const papaResults = Papa.parse(unzippedCsv, { header: true });

            //console.log('getLetterboxdURIsFromJSZip()', papaResults);

            if ( !Array.isArray(papaResults?.data) ) {
              throw new Error(`getLetterboxdURIsFromJSZip() could not parse the '${_documentName}' document: ${papaResults?.errors}`);
            }
            const filmArray = papaResults.data;

            //console.log('getLetterboxdURIsFromJSZip()', filmArray);

            // create an array only containing the Letterboxd URIs -- and only the last part of their paths
            const uriArray = filmArray.map(film => {
              const fullUri = film['Letterboxd URI'];
              if (fullUri) {
                const indexOfLastSlash = fullUri.lastIndexOf('/');
                if (indexOfLastSlash > -1) {
                  return fullUri.substring(indexOfLastSlash + 1);
                }
              }
            }).filter(e => e);

            //console.log('getLetterboxdURIsFromJSZip()', uriArray);
            return uriArray;
          });
    },

    onMarkFilmsViewed() {

      const buttonElement = this.$refs['import-viewed-films-button'];

      buttonElement.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> importing...`
      buttonElement.disabled = true;

      // this endpoint expects an array of objects like this:
      //  {
      //      film_id: <the film id>,
      //      viewed: <true or false>
      //  }
      const film_viewed_states = this.filmsToMarkViewed.map(f => {
        return { film_id: f._id, viewed: true }
      });

      return axios.post('/api/user/set-viewed-states', {
        film_viewed_states: film_viewed_states
      })
          .then(response => {
            userStore.setUser(response.data);
            buttonElement.textContent = 'done';
            analytics.sendEvent('letterboxd_data_imported', {
              watched: this.filmsToMarkViewed.map(f => f._id)
            });
          })
          .catch(err => {
            //console.log(err);
            analytics.notifyBugsnagOfError(err);
            buttonElement.textContent = 'error';
          });
    },

    onMarkFilmsBookmarked() {

      const buttonElement = this.$refs['import-bookmarked-films-button'];

      //buttonElement.textContent = 'working...';
      buttonElement.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> importing...`
      buttonElement.disabled = true;

      // this endpoint expects an array of objects like this:
      //  {
      //      film_id: <the film id>,
      //      bookmarked: <true or false>
      //  }
      const film_bookmarked_states = this.filmsToMarkBookmarked.map(f => {
        return { film_id: f._id, bookmarked: true }
      });

      return axios.post('/api/user/set-bookmarked-states', {
        film_bookmarked_states: film_bookmarked_states
      })
          .then(response => {
            userStore.setUser(response.data);
            buttonElement.textContent = 'done';
            analytics.sendEvent('letterboxd_data_imported', {
              watchlist: this.filmsToMarkBookmarked.map(f => f._id)
            });
          })
          .catch(err => {
            //console.log(err);
            analytics.notifyBugsnagOfError(err);
            buttonElement.textContent = 'error';
          });
    }
  }
}
</script>


<style scoped>
.container-fluid {
  padding-top: 20px;
  &.showing-message-banner {
    padding-top: 70px;
  }
}

.mol-center-card-width {

  text-align: center;

  .title-text {
    padding-bottom: 50px;
    h2 {
      margin-bottom: -0.375em;
    }
  }
}
:deep(.letterboxd-icon) {
  height: 5em;
}

.membership-message {
  margin-top:2.25em;
  margin-bottom: 3em;
  a {
    text-decoration: inherit;
    color: inherit;
    font-weight: 700;

    &:hover {
      color: var(--bs-link-hover-color);
    }
  }
}


.export-from-letterboxd {
  .import-step {
    margin-top:2.25em;
    margin-bottom: 3em;
  }

  .account-settings-link {
    text-decoration: inherit;
    color: var(--bs-link-hover-color);
    font-weight: 500;

    .fa-external-link {
      margin-left: 0.1em;
    }
  }

  .export-your-data {
    text-transform: uppercase;
    font-size: 80%;
    font-weight: 600;

    border: solid;
    border-color: #dcdcdc;
    border-width: 1px;
    border-radius: 5px;
    padding: 1.5px 5px 3px 5px;

    box-shadow: inset 0 1px rgba(255, 255, 255, .2);
    letter-spacing: 0.075em;
  }

  .import-button {
    margin-bottom: 1rem;
    input {
      display: none;
    }
  }
}



.import-to-myoscarlist {

  .import-watched-films {
    margin-bottom: 5rem;
  }

  .import-bookmarked-films {
    margin-top: 5rem;
    margin-bottom: 5rem;
  }

  .import-button-div {
    font-weight: bold;
    margin-top: 1rem;

    .fa-bookmark {
      display: inline-block;
      color: coral;
    }

  }

  .letterboxd-watched {
    /*
    color: #bcd;
    background-color: #556677;
     */

    font-size: 80%;
    font-weight: 600;
    text-transform: uppercase;
    border: solid;
    border-color: #dcdcdc;
    border-width: 1px;
    border-radius: 3px;
    padding: 1.5px 5px 3px 5px;

    letter-spacing: 0.075em;

    img {
      vertical-align: text-bottom;
      height: 1.2em;
    }
  }


  .expandable-card {

    background-color: var(--bs-tertiary-bg);

    .expandable-card-header {
      h5 {
      }
    }

    .film-list {
      margin-top: 1em;
      columns: 3 auto;
      text-align: left;
      font-size: xx-small;

      .check-in-box {
        vertical-align: text-bottom;
        height: 1em;
      }

      .fa-bookmark {
        display: inline-block;
        color: coral;
        font-size: 50%;
        text-indent: 0px;
        padding-left: 0.3em;

        vertical-align: super;
      }
    }
  }

}

</style>